import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AppButton from "../../components/form/AppButton";
import { AuthPageShell } from "../../components/layout";
import sessionAuthApi, { type InvitePreview } from "../../services/sessionAuth";
import authService from "../../services/authService";
import { workspaceService } from "../../services/workspaceService";
import { authEntryButtonSx } from "../login/authEntryButtonSx";

export default function InviteUI() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || params.get("invite") || "";
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("auth.invite.missing"));
      return;
    }
    sessionAuthApi
      .previewInvite(token)
      .then(setPreview)
      .catch(() => setError(t("auth.invite.invalid")));
  }, [token, t]);

  const accept = async () => {
    setAccepting(true);
    setError(null);
    try {
      await workspaceService.acceptInvite(token);
      navigate("/report");
    } catch (e: any) {
      setError(e?.message || t("auth.invite.acceptFailed"));
    } finally {
      setAccepting(false);
    }
  };

  const goRegister = () => navigate(`/register?invite=${encodeURIComponent(token)}`);
  const goLogin = () => navigate(`/login?invite=${encodeURIComponent(token)}`);

  return (
    <AuthPageShell
      maxWidth={480}
      title={t("auth.invite.title")}
      footer={<Link to="/login">{t("auth.invite.backToLogin")}</Link>}
    >
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {!preview && !error && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}
        {preview && (
          <>
            <Typography variant="body2" color="text.secondary">
              {t("auth.invite.summary", {
                workspace: preview.workspaceName || t("auth.invite.workspaceFallback"),
                role: t(`workspace.roles.${preview.role || "USER"}`),
              })}
            </Typography>
            {preview.status === "ACCEPTED" && (
              <Alert severity="info">{t("auth.invite.alreadyAccepted")}</Alert>
            )}
            {preview.status === "EXPIRED" && (
              <Alert severity="warning">{t("auth.invite.expired")}</Alert>
            )}
            {preview.status === "PENDING" && (
              <InviteActions
                email={preview.email}
                accountExists={Boolean(preview.accountExists)}
                accepting={accepting}
                onAccept={accept}
                onRegister={goRegister}
                onLogin={goLogin}
              />
            )}
          </>
        )}
      </Stack>
    </AuthPageShell>
  );
}

function InviteActions({
  email,
  accountExists,
  accepting,
  onAccept,
  onRegister,
  onLogin,
}: {
  email?: string;
  accountExists: boolean;
  accepting: boolean;
  onAccept: () => void;
  onRegister: () => void;
  onLogin: () => void;
}) {
  const { t } = useTranslation();
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    authService.getUser().then((user) => setLoggedIn(Boolean(user)));
  }, []);

  if (loggedIn) {
    return (
      <AppButton variant="contained" fullWidth onClick={onAccept} loading={accepting} sx={authEntryButtonSx}>
        {t("auth.invite.accept")}
      </AppButton>
    );
  }
  return (
    <Stack spacing={2}>
      {email && (
        <Typography variant="body2">
          {t("auth.invite.sentTo", { email })}
        </Typography>
      )}
      {accountExists ? (
        <AppButton variant="contained" fullWidth onClick={onLogin} sx={authEntryButtonSx}>
          {t("auth.invite.signInToAccept")}
        </AppButton>
      ) : (
        <AppButton variant="contained" fullWidth onClick={onRegister} sx={authEntryButtonSx}>
          {t("auth.invite.createAccount")}
        </AppButton>
      )}
    </Stack>
  );
}
